<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentContainer
      class="hero"
      :start="1"
      :end="1"
    >
      <UiComponentTag type="secondary">
        {{ props.hub.heroTitle }}
      </UiComponentTag>
      <UiComponentHeading>{{ props.page.heroTitle }}</UiComponentHeading>
      <UiComponentParagraph>{{ props.page.heroText }}</UiComponentParagraph>
    </UiComponentContainer>

    <!-- Section > Content -->
    <UiComponentContainer class="content">
      <UiComponentWrapper class="main">
        <OrganismRichText
          class="article"
          :content="content"
          :primary-button="signupOrUpgradeCallToAction"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="aside">
        <UiComponentWrapper class="related-articles">
          <UiComponentHeading
            as="paragraph"
            :level="4"
          >
            {{ props.hub.sharedWordingRelatedContent }}
          </UiComponentHeading>
          <UiComponentWrapper class="list">
            <NuxtLink
              v-for="article in allRelatedArticles"
              :key="article.id"
              :to="getArticleUrl(article)"
            >
              {{ article.title }}
            </NuxtLink>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper
          class="request-demo"
          @click="signupOrUpgrade"
        >
          <UiComponentImage
            v-if="
              props.componentGetStarted.background
                && getStartedBackgroundHeight
                && getStartedBackgroundWidth
            "
            :key="`${getStartedBackgroundHeight}x${getStartedBackgroundWidth}`"
            :alt="props.componentGetStarted.background.alt"
            class="background"
            :expected-size="getStartedBackgroundWidth"
            :extra-params="{
              fit: 'crop',
              h: getStartedBackgroundHeight,
              w: getStartedBackgroundWidth,
            }"
            :height="getStartedBackgroundHeight"
            :orientation="
              getStartedBackgroundHeight > getStartedBackgroundWidth
                ? 'portrait'
                : 'landscape'
            "
            :source="props.componentGetStarted.background.url"
            :width="getStartedBackgroundWidth"
          />
          <UiComponentWrapper class="inner">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{ props.componentGetStarted.title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ props.componentGetStarted.paragraph }}</UiComponentParagraph>
            <AtomButton
              :action="signupOrUpgradeCallToAction.action"
              label="Sidebar section CTA"
              :to="signupOrUpgradeCallToAction.to"
              type="text"
            >
              {{ signupOrUpgradeCallToAction.slot }}
              <UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="[
          props.componentGlobal.noCreditCard,
          props.componentGlobal.noInstall,
        ]"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
// Composables
const { track } = useAnalyticsJS()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Props
const props = defineProps({
  allArticles: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentPromotion: {
    required: true,
    type: Object,
  },
  hub: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
})

// States
const forms = useState('forms')

// Refs
const getStartedBackgroundHeight = ref(null)
const getStartedBackgroundWidth = ref(null)
const getStartedObserver = ref(null)

// Computed Properties
const allRelatedArticles = computed(() => {
  return [
    ...props.page.sidebarRelatedArticles,
    ...props.allArticles.map(({ heroTitle: title, ...props }) => ({
      ...props,
      title,
    })),
  ].slice(0, 4)
})

const content = computed(() => {
  const { content, promote } = props.page
  const copy = [...content].map((item) => {
    if (item.__typename === 'ContentCustomerStoryAndQuoteRecord') {
      return {
        ...item,
        cta: {
          slot: props.componentGlobal.readTheCustomerStory,
          to: localePath({
            name: 'customers-slug',
            params: {
              slug: item.customer.slug,
            },
          }),
        },
      }
    }
    else {
      return { ...item }
    }
  })

  const index
    = copy.findIndex(item => item.__typename === 'TextRecord') + 1

  if (promote) {
    copy.splice(index, 0, {
      __typename: 'CalltoactionRecord',
      cta: isLogged.value
        ? props.componentGlobal.ctaUpgradeNow
        : props.componentGlobal.ctaTryForFreeShort,
      full: true,
      id: '1',
      link: isLogged.value
        ? `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`
        : `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
      patternDesktop: {
        ...props.componentPromotion.patternDesktop,
      },
      patternMobile: {
        ...props.componentPromotion.patternMobile,
      },
      title: props.componentPromotion.title,
      text: props.componentPromotion.text,
    })
  }

  return copy
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaTryForFreeShort,
      }
})

// Methods
function getArticleUrl(article) {
  switch (true) {
    case article.__typename === 'BlogPostRecord':
      return localePath({
        name: 'blog-slug',
        params: { slug: article.slug },
      })
    case article.__typename === 'GlossaryArticleRecord':
      return localePath({
        name: 'webinar-glossary-slug',
        params: { slug: article.slug },
      })
    case article.__typename === 'PillarPageRecord':
      return localePath({
        name: 'resources-guides-slug',
        params: { slug: article.slug },
      })
    case article.__typename === 'ProductPageRecord':
      switch (true) {
        case article.slug === 'online-meeting-software':
          return localePath({
            name: 'online-meeting-software',
          })
        case article.slug === 'virtual-event-software':
          return localePath({
            name: 'virtual-event-software',
          })
        case article.slug === 'webinar-software':
          return localePath({
            name: 'webinar-software',
          })
        case article.slug === 'automated-webinar':
          return localePath({
            name: 'webinar-software-automated-webinar',
          })
        case article.slug === 'on-demand-webinar':
          return localePath({
            name: 'webinar-software-on-demand',
          })
      }
      break
    case article.__typename === 'SinglePageRecord':
      return localePath({
        name: 'slug',
        params: { slug: article.slug },
      })
    case article.__typename === 'UseCaseRecord':
      return localePath({
        name: 'use-cases-slug',
        params: { slug: article.slug },
      })
    case article.__typename === 'VideoGlossaryArticleRecord':
      return localePath({
        name: 'video-glossary-slug',
        params: { slug: article.slug },
      })
    default:
      return undefined
  }
}

function observeGetStartedBlock() {
  const { offsetHeight, offsetWidth } = document.querySelector(
    '.page .content .aside .request-demo',
  )
  getStartedBackgroundHeight.value = offsetHeight
  getStartedBackgroundWidth.value = offsetWidth
}

function signupOrUpgrade(event) {
  if (!event.target.classList.contains('button') && !event.target.classList.contains('button')) {
    event.currentTarget.querySelector('.button').click()
  }
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (getStartedObserver.value) {
    getStartedObserver.value.unobserve(
      document.querySelector('.page .content .aside .request-demo'),
    )
  }
})

onMounted(() => {
  getStartedObserver.value = new ResizeObserver(observeGetStartedBlock)
  getStartedObserver.value.observe(
    document.querySelector('.page .content .aside .request-demo'),
  )
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center;

  .hero {
    @apply flex-col items-center pt-10 pb-6;

    @screen md {
      @apply pt-16;
    }

    .tag {
      @apply self-center;
    }

    .h1 {
      @apply mt-6 text-center;
    }

    p {
      @apply max-w-[860px] mt-4 text-grey-blue-600 text-center leading-7;
    }
  }

  .content {
    @apply flex-col mt-12 gap-[150px];

    @screen md {
      @apply mt-20;
    }

    @screen xl {
      @apply flex-row flex-wrap justify-between;
    }

    .main {
      @apply flex-col;

      @screen xl {
        @apply w-span8;
      }

      :deep(.article) {
        .call-to-action-record {
          @screen xl {
            @apply px-0 !important;
          }
        }

        .image-with-text-record {
          .content {
            p {
              @apply text-grey-blue-600 !important;
            }
          }
        }

        .text-record {
          li,
          p {
            @apply text-grey-blue-600;
          }
        }
      }
    }

    .aside {
      @apply flex-col gap-16;

      @screen xl {
        @apply sticky top-24 self-start w-span3;
      }

      .related-articles {
        @apply flex-col gap-6;

        .list {
          @apply flex-col gap-4;

          a {
            @apply font-medium text-base text-winter-green-900 leading-6 transition-colors duration-500 ease-out;

            &:hover {
              @apply text-livestorm-blue-700;
            }
          }
        }
      }

      .request-demo {
        @apply hidden;

        @screen xl {
          @apply relative flex p-8 rounded-4xl shadow-light4 cursor-pointer;
        }

        &:hover {
          .inner {
            .button {
              :deep(i) {
                @apply translate-x-2;
              }
            }
          }
        }

        .background {
          @apply absolute inset-0 bg-livestorm-blue-700 rounded-4xl z-0;
        }

        .inner {
          @apply flex-col z-10;

          .h5 {
            @apply text-white;
          }

          .h5 + p {
            @apply mt-4 text-white text-origin leading-7;
          }

          .button {
            @apply self-start h-[50px] mt-2 px-0 font-bold text-white;

            i {
              @apply text-white text-base leading-none;
            }
          }
        }
      }
    }
  }

  .get-started {
    @apply mt-12;

    @screen md {
      @apply mt-20;
    }
  }
}
</style>
